import { Navigate, Route } from 'react-router-dom';

import { AuthSplash, useAuth } from '@/domains/auth';
import {
  AddSurveyPage,
  DocumentsPage,
  HomePage,
  MySurveysPage,
  PORTAL_ROOT,
  ProfilePage,
  ResponseFormPage,
  ResponseSubmittedPage,
  TeamRoutingPage
} from '@/domains/survey';
import { PortalBoundary } from '../boundary';
import { AccountPage } from './account-page';
import { PortalShell } from './portal-shell';

/**
 * The Advisor Portal's routes, `/portal/*` (the two-portals design, §3 and §6) — its own module,
 * mounted under `ProtectedRoute` beside the Team Portal's and sharing nothing with it but the
 * guard. Help is not here: it is reachable signed out, and is mounted beside the front door
 * (`HelpRoute`).
 *
 * The zone's element is the guard and the shell in one. An Advisor Portal account gets the shell;
 * a Team Portal account, whatever its roles, gets the boundary page at the address it opened —
 * never an empty advisor home.
 * That is a render decision: the respondent routes survey-api serves are scoped to the caller's
 * own participants whatever this renders.
 */
export function portalRoutes() {
  return (
    <Route path={PORTAL_ROOT} element={<PortalZone />}>
      <Route index element={<HomePage />} />
      <Route path="surveys">
        <Route index element={<MySurveysPage />} />
        {/* Registration inside the account (the teams onboarding plan's R7): the same page as
            `/registration`, signed in. No role guard of its own — the zone's element above is
            it, and survey-api's binding of a run to any session is why staff must not get here. */}
        <Route path="add" element={<AddSurveyPage />} />
        {/* A team's own routing questions (R9) — where the catalogue sends a team survey-api has
            routed nowhere yet. */}
        <Route path="teams/:participantId/routing" element={<TeamRoutingPage />} />
        <Route path=":responseId" element={<ResponseFormPage />} />
        <Route path=":responseId/submitted" element={<ResponseSubmittedPage />} />
      </Route>
      <Route path="profile" element={<ProfilePage />} />
      <Route path="documents" element={<DocumentsPage />} />
      <Route path="account" element={<AccountPage />} />
      <Route path="*" element={<Navigate to={PORTAL_ROOT} replace />} />
    </Route>
  );
}

function PortalZone() {
  const { user } = useAuth();
  if (!user) return <AuthSplash />;
  if (user.kind !== 'advisor') return <PortalBoundary />;
  return <PortalShell />;
}
